import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Epic } from '../../../types/Epic';
import { TaskStatus, TaskStatusGroup, sortTaskStatusesByOrder } from '../../../types/TaskStatus';
import { Filter, FilterOption } from '../../Modal/TaskFiltersModal';
import { TaskEstimateMapping, PriorityOptions } from '../../TaskTable/helpers/helper';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';
import { User } from '../../../types/User';
import { isEqual } from 'lodash';
import { TaskType, TASK_TYPE_ORDER } from '../../../types/TaskType';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../../../types/WorkspaceView';
import { SpaceLabel } from '../../../types/TaskLabel';
import { isInActiveSprint } from '../../../utils/taskListUtils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Title-only task search (#1612).
 * Tokenizes the query on whitespace (collapsing repeated spaces), then requires
 * every token to appear somewhere in the title as a case-insensitive substring.
 * Tokens use AND logic and are order-independent — e.g. "creat fix" matches
 * "Fix creation bug". No other task fields are considered.
 */
export function matchesSearch(title: string, searchValue: string): boolean {
  const tokens = searchValue.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = (title ?? '').toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

export function applySearchAndFiltersWithParents(
  selectedFilters: Filter[],
  dataSource: TaskTableDataType[],
  searchValue: string,
  currentUserId: number | undefined,
  estimationType: WorkspaceEstimationType = WorkspaceEstimationType.storyPoints,
  activeSprintId?: number | null
): TaskTableDataType[] {
  // Early return if no filters or search
  if ((!selectedFilters || selectedFilters.length === 0) && !searchValue) {
    return dataSource;
  }

  const result: TaskTableDataType[] = [];

  for (const task of dataSource) {
    let parentMatches = false;
    let matchingSubtasks: TaskTableDataType[] = [];

    // Check parent task match
    if (matchesTaskFilters(task, selectedFilters, searchValue, currentUserId, estimationType, activeSprintId)) {
      parentMatches = true;
    }

    // Check subtasks if they exist
    if (task.subtasks?.length > 0) {
      // Filter subtasks directly without creating intermediate arrays
      matchingSubtasks = task.subtasks
        .filter((subtask) =>
          matchesTaskFilters(
            { ...subtask, key: subtask.id } as TaskTableDataType,
            selectedFilters,
            searchValue,
            currentUserId,
            estimationType,
            activeSprintId
          )
        )
        .map((subtask) => ({ ...subtask, key: subtask.id }) as TaskTableDataType);
    }

    // Include task if parent matches or has matching subtasks.
    // Epics are the dataSource parents only in the Epics module, where search must
    // apply to the epic name alone — never surface an epic just because one of its
    // items/subitems matched. Regular tasks still show as context parents. #2527
    const includeForChildMatch = matchingSubtasks.length > 0 && task.type !== TaskType.epic;
    if (parentMatches || includeForChildMatch) {
      result.push({
        ...task,
        // Subitems must also satisfy the active filter (#2603) — a matching parent
        // no longer drags in every subitem, only the ones that themselves match.
        subtasks: matchingSubtasks,
        ...(!parentMatches &&
          includeForChildMatch && {
            isIncludedForChildContext: true,
            isDisabledInFilter: true,
          }),
      });
    }
  }

  return result;
}

/**
 * Drops parents that are present only as hierarchy context (#2324).
 *
 * `applySearchAndFiltersWithParents` and `buildGroupTasks` keep a parent that does not
 * match on its own when one of its subitems does, rendered dimmed so the hierarchy is
 * visible. When the view's Display setting hides subitems, that matching subitem is never
 * shown, so the dimmed parent has nothing to give context to and must go too. Parents
 * that match the filters themselves are kept.
 */
export function removeChildContextParents(tasks: TaskTableDataType[]): TaskTableDataType[] {
  return tasks.filter((task) => !task.isIncludedForChildContext);
}

/**
 * Builds the task list for a single group when grouping the table view (#2309).
 *
 * `belongsToGroup` tests a SINGLE task (parent OR subtask) against this group's value.
 * For each parent in `tasks`:
 *  - If the parent belongs here, it's shown normally — but its subtasks are narrowed to
 *    only those that ALSO belong here (subitems of other groups move to their own group).
 *  - If the parent does NOT belong but at least one subtask does, the parent is "borrowed"
 *    into this group purely as hierarchy context: rendered dimmed (`isDisabledInFilter`)
 *    and read-only (`isGroupContext` excludes it from drag/selection), with only the
 *    matching subitems under it (which inherit the read-only `isGroupContext` flag).
 *  - Otherwise the parent contributes nothing to this group.
 *
 * Input is expected to already be narrowed by `applySearchAndFiltersWithParents`, so the
 * subitems surfaced here satisfy both the active filter/search and the group value.
 */
export function buildGroupTasks(
  tasks: TaskTableDataType[],
  belongsToGroup: (task: TaskTableDataType) => boolean
): TaskTableDataType[] {
  const result: TaskTableDataType[] = [];

  for (const task of tasks) {
    const parentBelongs = belongsToGroup(task);
    const matchingSubtasks = (task.subtasks ?? []).filter((subtask) =>
      belongsToGroup(subtask as TaskTableDataType)
    );

    if (parentBelongs) {
      result.push({ ...task, subtasks: matchingSubtasks });
    } else if (matchingSubtasks.length > 0) {
      result.push({
        ...task,
        subtasks: matchingSubtasks.map(
          (subtask) => ({ ...subtask, isGroupContext: true }) as TaskTableDataType
        ),
        isIncludedForChildContext: true,
        isDisabledInFilter: true,
        isGroupContext: true,
      });
    }
  }

  return result;
}

/**
 * Helper function to check if a single task matches the filters
 * Optimized to avoid array operations for single task checking
 */
function matchesTaskFilters(
  task: TaskTableDataType,
  selectedFilters: Filter[],
  searchValue: string,
  currentUserId: number | undefined,
  estimationType: WorkspaceEstimationType,
  activeSprintId?: number | null
): boolean {
  // Apply search first (fastest filter)
  if (searchValue && !matchesSearch(task.title, searchValue)) {
    return false;
  }

  // Apply filters
  if (!selectedFilters?.length) {
    return true;
  }

  return selectedFilters.every((filter) => {
    const selectedOptions = filter.options.filter((option) => option.selected);
    if (selectedOptions.length === 0) return true;

    switch (filter.type) {
      case 'taskType':
        return selectedOptions.some((option) => task.type === option.metadata);

      case 'sprintStatus':
        // Derive from the SAME authoritative sprint membership the Sprint module uses
        // (sprintId === activeSprintId), so "Stage = Sprint" in Backlog and the Sprint
        // module always agree. A stale stage:'sprint' (non-active sprintId) counts as
        // Backlog, mirroring the Stage column. #2598
        return selectedOptions.some((option) => {
          const metadata = option.metadata;
          const inSprint = isInActiveSprint(task, activeSprintId);
          if (metadata === 'sprint' || metadata === 'kanban') return inSprint;
          if (inSprint) return false;
          if (metadata === 'planning') return task.stage === 'planning';
          if (metadata === 'none') return task.stage === 'backlog' || task.stage === 'sprint';
          return false;
        });

      case 'epic':
        const hasNoneEpic = selectedOptions.some((option) => option.name === 'No Epic Assigned');
        const hasSpecificEpics = selectedOptions.some(
          (option) => option.name !== 'No Epic Assigned' && task.epicId === (option.metadata as Epic)?.id
        );
        return (hasNoneEpic && !task.epicId) || hasSpecificEpics;

      case 'owner':
        const hasMe = selectedOptions.some((option) => option.name === 'Me');
        const hasNone = selectedOptions.some((option) => option.name === 'No Owner');
        const hasSpecificUsers = selectedOptions.some(
          (option) => option.metadata && task.assignee === (option.metadata as User)?.id
        );
        return (hasMe && task.assignee === currentUserId) || (hasNone && !task.assignee) || hasSpecificUsers;

      case 'status':
        return selectedOptions.some((option) => task.statusId === (option.metadata as TaskStatus).id);

      case 'priority':
        return selectedOptions.some((option) => task.priority === option.metadata);

      case 'startDate':
        return (
          filterTasksByDate(
            [task],
            selectedOptions.map((o) => o.name),
            true
          ).length > 0
        );

      case 'deadline':
        return (
          filterTasksByDate(
            [task],
            selectedOptions.map((o) => o.name),
            false
          ).length > 0
        );

      case 'estimate':
        const hasNoEstimate = selectedOptions.some((option) => !option.metadata);
        const hasSpecificEstimates = selectedOptions.some((option) => {
          if (!option.metadata) return false;
          return estimationType === WorkspaceEstimationType.storyPoints
            ? task.storyPoints === option.metadata
            : task.estimate === option.metadata;
        });
        const taskHasNoEstimate =
          estimationType === WorkspaceEstimationType.tshirtSizes ? !task.estimate : !task.storyPoints;
        return (hasNoEstimate && taskHasNoEstimate) || hasSpecificEstimates;

      case 'labels':
        const hasNoLabel = selectedOptions.some((option) => option.name === 'No Label');
        const hasSpecificLabels = selectedOptions.some(
          (option) =>
            option.metadata &&
            task.TaskLabels?.some((taskLabel) => taskLabel.labelId === (option.metadata as SpaceLabel)?.id)
        );
        const taskHasNoLabels = !task.TaskLabels || task.TaskLabels.length === 0;
        return (hasNoLabel && taskHasNoLabels) || hasSpecificLabels;

      default:
        return true;
    }
  });
}

export function applySearchAndFilters(
  selectedFilters: Filter[],
  dataSource: TaskTableDataType[],
  searchValue: string,
  currentUserId: number | undefined,
  estimationType: WorkspaceEstimationType = WorkspaceEstimationType.storyPoints,
  activeSprintId?: number | null
): TaskTableDataType[] {
  const searchedDataSource = applySearch(dataSource, searchValue);
  if (!selectedFilters || !Array.isArray(selectedFilters) || selectedFilters.length === 0) {
    return searchedDataSource;
  }

  let filteredDataSource = searchedDataSource;

  selectedFilters.forEach((filter) => {
    if (filter.type === 'taskType') {
      const filterTaskTypes = filter.options.filter((taskType) => taskType.selected);

      filteredDataSource = filteredDataSource.filter((item) => {
        return !!filterTaskTypes.find((taskType) => item.type === taskType.metadata);
      });
    }
    if (filter.type === 'sprintStatus') {
      const filterSprintStatuses = filter.options.filter((sprintStatus) => sprintStatus.selected);
      filteredDataSource = filteredDataSource.filter((item) => {
        return !!filterSprintStatuses.find((sprintStatus) => {
          // Authoritative sprint membership (sprintId === activeSprintId), matching the
          // Sprint module and the Stage column. #2598
          const inSprint = isInActiveSprint(item, activeSprintId);
          if (sprintStatus.metadata === 'sprint' || sprintStatus.metadata === 'kanban') return inSprint;
          if (inSprint) return false;
          if (sprintStatus.metadata === 'planning') return item.stage === 'planning';
          if (sprintStatus.metadata === 'none') return item.stage === 'backlog' || item.stage === 'sprint';
          return false;
        });
      });
    }
    if (filter.type === 'epic') {
      const filterEpics = filter.options.filter((epic) => epic.selected && epic.name !== 'No Epic Assigned');
      const noneFilter = filter.options.find((epic) => epic.selected && epic.name === 'No Epic Assigned');

      filteredDataSource = filteredDataSource.filter((item) => {
        if (noneFilter && !item.epicId) {
          return true;
        }
        return !!filterEpics.find((epic) => item.epicId === epic.metadata?.id);
      });
    }
    if (filter.type === 'owner') {
      const filterUsers = filter.options.filter((option) => option.selected && option.metadata);
      const noneFilter = filter.options.find((option) => option.selected && option.name === 'No Owner');
      const meFilter = filter.options.find((option) => option.selected && option.name === 'Me');

      filteredDataSource = filteredDataSource.filter((item) => {
        if (meFilter && item.assignee === currentUserId) {
          return true;
        }
        if (noneFilter && !item.assignee) {
          return true;
        }
        return !!filterUsers.find((user) => item.assignee === user.metadata?.id);
      });
    }
    if (filter.type === 'status') {
      const filterStatuses = filter.options.filter((status) => status.selected);

      filteredDataSource = filteredDataSource.filter((item) => {
        return !!filterStatuses.find((status) => item.statusId === status.metadata.id);
      });
    }
    if (filter.type === 'priority') {
      const filterPriorities = filter.options.filter((priority) => priority.selected);

      filteredDataSource = filteredDataSource.filter((item) => {
        return !!filterPriorities.find((priority) => item.priority === priority.metadata);
      });
    }
    if (filter.type === 'startDate') {
      const filterStartDates = filter.options.filter((startDate) => startDate.selected).map((option) => option.name);
      filteredDataSource = filterTasksByDate(filteredDataSource, filterStartDates, true);
    }
    if (filter.type === 'deadline') {
      const filterDeadlines = filter.options.filter((priority) => priority.selected).map((option) => option.name);
      filteredDataSource = filterTasksByDate(filteredDataSource, filterDeadlines, false);
    }
    if (filter.type === 'estimate') {
      const filterEstimate = filter.options.filter((estimate) => estimate.selected && estimate.metadata);
      const noneFilter = filter.options.find((estimate) => estimate.selected && !estimate.metadata);

      filteredDataSource = filteredDataSource.filter((item) => {
        if (
          noneFilter &&
          ((estimationType === WorkspaceEstimationType.tshirtSizes && !item.estimate) ||
            (estimationType === WorkspaceEstimationType.storyPoints && !item.storyPoints))
        ) {
          return true;
        }
        return !!filterEstimate.find(
          (estimate) =>
            (estimationType === WorkspaceEstimationType.storyPoints ? item.storyPoints : item.estimate) ===
            estimate.metadata
        );
      });
    }
    if (filter.type === 'labels') {
      const filterLabels = filter.options.filter((label) => label.selected && label.metadata);
      const noneFilter = filter.options.find((label) => label.selected && label.name === 'No Label');

      filteredDataSource = filteredDataSource.filter((item) => {
        if (noneFilter && (!item.TaskLabels || item.TaskLabels.length === 0)) {
          return true;
        }
        if (!item.TaskLabels || item.TaskLabels.length === 0) {
          return false;
        }
        // Check if task has any of the selected labels
        return !!filterLabels.find((filterLabel) =>
          item.TaskLabels?.some((taskLabel) => taskLabel.labelId === (filterLabel.metadata as SpaceLabel)?.id)
        );
      });
    }
  });

  return filteredDataSource;
}

export function applySearch(dataSource: TaskTableDataType[], searchValue: string): TaskTableDataType[] {
  return searchValue ? dataSource.filter((t) => matchesSearch(t.title, searchValue)) : dataSource;
}

export function filterTasksByDate(tasks: TaskTableDataType[], filters: string[], isStartDate: boolean) {
  const today = dayjs().startOf('day');
  const yesterday = dayjs().subtract(1, 'day').startOf('day');
  const tomorrow = dayjs().add(1, 'day').startOf('day');
  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');
  const lastWeekStart = startOfWeek.subtract(1, 'week');
  const lastWeekEnd = endOfWeek.subtract(1, 'week');
  const nextWeekStart = startOfWeek.add(1, 'week');
  const nextWeekEnd = endOfWeek.add(1, 'week');
  const startOfMonth = dayjs().startOf('month');
  const endOfMonth = dayjs().endOf('month');
  const lastMonthStart = startOfMonth.subtract(1, 'month');
  const lastMonthEnd = endOfMonth.subtract(1, 'month');
  const nextMonthStart = startOfMonth.add(1, 'month');
  const nextMonthEnd = endOfMonth.add(1, 'month');

  // Filter function to check each task's deadline
  return tasks.filter((task) => {
    const date = isStartDate ? task.startDate : task.deadline;
    const dateObj = date ? dayjs(date).startOf('day') : null;

    return filters.some((filter) => {
      switch (filter) {
        case 'Today':
          return dateObj && dateObj.isSame(today, 'day');
        case 'Yesterday':
          return dateObj && dateObj.isSame(yesterday, 'day');
        case 'Tomorrow':
          return dateObj && dateObj.isSame(tomorrow, 'day');
        case 'This week':
          return dateObj && dateObj.isSameOrAfter(startOfWeek) && dateObj.isSameOrBefore(endOfWeek);
        case 'Last week':
          return dateObj && dateObj.isSameOrAfter(lastWeekStart) && dateObj.isSameOrBefore(lastWeekEnd);
        case 'Next week':
          return dateObj && dateObj.isSameOrAfter(nextWeekStart) && dateObj.isSameOrBefore(nextWeekEnd);
        case 'This month':
          return dateObj && dateObj.isSameOrAfter(startOfMonth) && dateObj.isSameOrBefore(endOfMonth);
        case 'Last month':
          return dateObj && dateObj.isSameOrAfter(lastMonthStart) && dateObj.isSameOrBefore(lastMonthEnd);
        case 'Next month':
          return dateObj && dateObj.isSameOrAfter(nextMonthStart) && dateObj.isSameOrBefore(nextMonthEnd);
        case 'Past dates':
          return dateObj && dateObj.isBefore(today);
        case 'Future dates':
          return dateObj && dateObj.isAfter(today);
        case 'No Date':
          return !dateObj;
        default:
          return true;
      }
    });
  });
}

export function getDefaultFilters(
  epics: Epic[],
  users: User[],
  statusOptions: TaskStatus[],
  labels: SpaceLabel[],
  estimationType: WorkspaceEstimationType = WorkspaceEstimationType.storyPoints,
  framework: WorkspaceFramework,
  viewGroup: WorkspaceViewGroup,
  viewEntityType: WorkspaceViewEntityType
): Filter[] {
  const sprintKanbanOptions: FilterOption[] = [
    ...(framework === WorkspaceFramework.scrum
      ? [
          {
            type: 'sprintStatus' as const,
            name: 'Planning',
            selected: false,
            metadata: 'planning',
          },
          {
            type: 'sprintStatus' as const,
            name: 'Sprint',
            selected: false,
            metadata: 'sprint',
          },
        ]
      : [
          {
            type: 'sprintStatus' as const,
            name: 'Kanban',
            selected: false,
            metadata: 'kanban',
          },
        ]),
    {
      type: 'sprintStatus' as const,
      name: 'No Sprint',
      selected: false,
      metadata: 'none',
    },
  ];
  const epicOptions: FilterOption[] = epics.map((epic) => {
    return {
      type: 'epic' as const,
      name: epic.title,
      selected: false,
      metadata: epic,
    };
  });
  epicOptions.push({
    type: 'epic' as const,
    name: 'No Epic Assigned',
    selected: false,
    metadata: {
      id: -5,
      name: 'No Epic Assigned',
    },
  });

  const userOptions: FilterOption[] = users.map((user) => {
    return {
      type: 'owner' as const,
      name: user.fullName || user.email,
      selected: false,
      metadata: user,
    };
  });
  userOptions.unshift({
    type: 'owner' as const,
    name: 'Me',
    selected: false,
    metadata: null,
  });
  userOptions.push({
    type: 'owner' as const,
    name: 'No Owner',
    selected: false,
    metadata: null,
  });

  const labelOptions: FilterOption[] = labels.map((label) => {
    return {
      type: 'labels' as const,
      name: label.title,
      selected: false,
      metadata: label,
    };
  });
  labelOptions.push({
    type: 'labels' as const,
    name: 'No Label',
    selected: false,
    metadata: null,
  });

  const ownerFilter: Filter[] = [
    {
      type: 'owner' as const,
      name: 'Owner',
      isMultiselect: false,
      isSearchable: false,
      options: userOptions,
    },
  ];

  const estimateOptions: FilterOption[] = Object.keys(TaskEstimateMapping(estimationType)).map((keyStr) => {
    const key = Number(keyStr);
    return {
      type: 'estimate' as const,
      name: TaskEstimateMapping(estimationType)[key],
      selected: false,
      metadata: key,
    };
  });
  estimateOptions.push({
    type: 'estimate' as const,
    name: 'No estimate',
    selected: false,
    metadata: null,
  });

  const sprintStatusFilter = {
    type: 'sprintStatus' as const,
    name: 'Stage',
    isMultiselect: false,
    isSearchable: false,
    options: sprintKanbanOptions,
  } as Filter;

  const startDateFilter = {
    type: 'startDate' as const,
    name: 'Start Date',
    isMultiselect: false,
    isSearchable: false,
    options: [
      'Today',
      'Yesterday',
      'Tomorrow',
      'This week',
      'Last week',
      'Next week',
      'This month',
      'Last month',
      'Next month',
      'Past dates',
      'Future dates',
      'No Date',
    ].map((startDate) => {
      return {
        type: 'startDate' as const,
        name: startDate,
        selected: false,
        metadata: startDate,
      };
    }),
  };

  const deadlineFilter = {
    type: 'deadline' as const,
    name: 'Deadline',
    isMultiselect: false,
    isSearchable: false,
    options: [
      'Today',
      'Yesterday',
      'Tomorrow',
      'This week',
      'Last week',
      'Next week',
      'This month',
      'Last month',
      'Next month',
      'Past dates',
      'Future dates',
      'No Date',
    ].map((deadline) => {
      return {
        type: 'deadline' as const,
        name: deadline,
        selected: false,
        metadata: deadline,
      };
    }),
  };

  const shouldShowSprintStatusFilter =
    framework !== WorkspaceFramework.none &&
    viewGroup !== WorkspaceViewGroup.sprint &&
    viewGroup !== WorkspaceViewGroup.kanban &&
    viewGroup !== WorkspaceViewGroup.triage &&
    viewGroup !== WorkspaceViewGroup.archive;
  const sprintStatusFilters = shouldShowSprintStatusFilter ? [sprintStatusFilter] : [];

  if (viewEntityType === WorkspaceViewEntityType.epic) {
    return [...ownerFilter, startDateFilter, deadlineFilter];
  }

  return [
    {
      type: 'taskType',
      name: 'Type',
      isMultiselect: false,
      isSearchable: false,
      options: TASK_TYPE_ORDER.filter((taskType) => taskType !== TaskType.epic).map((taskType) => {
        return {
          type: 'taskType',
          name: taskType,
          selected: false,
          metadata: taskType,
        };
      }),
    },
    ...sprintStatusFilters,
    {
      type: 'epic',
      name: 'Epic',
      isMultiselect: false,
      isSearchable: false,
      options: epicOptions,
    },
    ...ownerFilter,
    {
      type: 'status',
      name: 'Status',
      isMultiselect: false,
      isSearchable: false,
      options: sortTaskStatusesByOrder(statusOptions).map((status) => {
        return {
          type: 'status',
          name: status.title,
          selected: false,
          metadata: status,
        };
      }),
    },
    {
      type: 'labels',
      name: 'Label',
      isMultiselect: true,
      isSearchable: true,
      options: labelOptions,
    },
    startDateFilter,
    deadlineFilter,
    {
      type: 'priority',
      name: 'Priority',
      isMultiselect: false,
      isSearchable: false,
      options: (Object.keys(PriorityOptions) as Array<keyof typeof PriorityOptions>).reverse().map((key) => {
        return {
          type: 'priority',
          name: PriorityOptions[key],
          selected: false,
          metadata: PriorityOptions[key],
        };
      }),
    },
    {
      type: 'estimate',
      name: 'Estimate',
      isMultiselect: false,
      isSearchable: false,
      options: estimateOptions,
    },
  ];
}

export function updateCurrentFilters(
  filters: Filter[],
  epics: Epic[],
  users: User[],
  statuses: TaskStatus[],
  labels: SpaceLabel[]
) {
  const updatedFilters = filters.map((filter) => {
    if (filter.type === 'epic') {
      const epicOptions: FilterOption[] = epics.map((epic) => {
        const currentFilterOption = filter.options.find((option) => option.metadata?.id === epic.id);
        return {
          type: 'epic',
          name: epic.title,
          selected: currentFilterOption?.selected ?? false,
          metadata: epic,
        };
      });
      const noneFilterOption = filter.options.find((option) => option.metadata?.id === -5);
      epicOptions.unshift({
        type: 'epic',
        name: 'No Epic Assigned',
        selected: noneFilterOption?.selected ?? false,
        metadata: {
          id: -5,
          name: 'No Epic Assigned',
        },
      });
      filter.options = epicOptions;
      return filter;
    } else if (filter.type === 'owner') {
      const userOptions: FilterOption[] = users.map((user) => {
        const currentFilterOption = filter.options.find((option) => option.metadata?.id === user.id);
        return {
          type: 'owner',
          name: user.fullName || user.email,
          selected: currentFilterOption?.selected ?? false,
          metadata: user,
        };
      });
      const noneFilterOption = filter.options.find((option) => option.name === 'No Owner');
      userOptions.push({
        type: 'owner',
        name: 'No Owner',
        selected: noneFilterOption?.selected ?? false,
        metadata: null,
      });
      const meFilterOption = filter.options.find((option) => option.name === 'Me');
      userOptions.unshift({
        type: 'owner',
        name: 'Me',
        selected: meFilterOption?.selected ?? false,
        metadata: null,
      });

      filter.options = userOptions;
      return filter;
    } else if (filter.type === 'status') {
      const statusOptions: FilterOption[] = statuses.map((status) => {
        const currentFilterOption = filter.options.find((option) => (option.metadata as TaskStatus).id === status.id);
        return {
          type: 'status',
          name: status.title,
          selected: currentFilterOption?.selected ?? false,
          metadata: status,
        };
      });

      const updatedFilter = { ...filter, options: statusOptions };
      return mergeStatusSelections(updatedFilter, filter); // Merge with current selections to handle group logic
    } else if (filter.type === 'labels') {
      const labelOptions: FilterOption[] = labels.map((label) => {
        const currentFilterOption = filter.options.find((option) => option.metadata?.id === label.id);
        return {
          type: 'labels',
          name: label.title,
          selected: currentFilterOption?.selected ?? false,
          metadata: label,
        };
      });
      const noneFilterOption = filter.options.find((option) => option.name === 'No Label');
      labelOptions.unshift({
        type: 'labels',
        name: 'No Label',
        selected: noneFilterOption?.selected ?? false,
        metadata: null,
      });

      filter.options = labelOptions;
      return filter;
    }
    return filter;
  });

  return updatedFilters;
}

function mergeStatusSelections(currentFilter: Filter, savedFilter: Filter): Filter {
  if (currentFilter.type !== 'status' || savedFilter.type !== 'status') {
    return currentFilter;
  }

  // Build map of saved selections and check group completeness
  const savedSelections = new Map<number, boolean>();
  const groupCompleteness = new Map<TaskStatusGroup, boolean>();

  savedFilter.options.forEach((opt) => {
    if (opt.type === 'status') {
      savedSelections.set(opt.metadata.id, opt.selected);
    }
  });

  // Check if each group was fully selected in saved filter
  [TaskStatusGroup.notStarted, TaskStatusGroup.inProgress, TaskStatusGroup.completed].forEach((group) => {
    const groupStatuses = savedFilter.options.filter((opt) => opt.type === 'status' && opt.metadata.group === group);
    const allSelected = groupStatuses.length > 0 && groupStatuses.every((opt) => opt.selected);
    groupCompleteness.set(group, allSelected);
  });

  // Apply to current options
  const updatedOptions = currentFilter.options.map((option) => {
    if (option.type === 'status') {
      const status = option.metadata;
      const wasSelected = savedSelections.get(status.id);
      // const groupWasComplete = groupCompleteness.get(status.group) || false;

      return {
        ...option,
        selected: wasSelected ?? option.selected, //!== undefined ? wasSelected : groupWasComplete,
      };
    }
    return option;
  });

  return { ...currentFilter, options: updatedOptions };
}

export function updateWithSpaceDefaultFilters(filters: Filter[], selectedFilters: Filter[]) {
  return filters.map((filter) => {
    const savedFilter = selectedFilters.find((saved) => saved.type === filter.type);
    if (!savedFilter) return filter;

    return filter.type === 'status' ? mergeStatusSelections(filter, savedFilter) : { ...savedFilter };
  });
}

export function updateOwnerFilter(filters: Filter[], ownerFilter: Filter | undefined) {
  if (!ownerFilter) {
    return;
  }

  const updatedFilters = filters.map((filter) => {
    if (filter.type === ownerFilter.type) {
      return ownerFilter;
    }
    return filter;
  });

  return updatedFilters;
}

export function clearOwnerFilter(filters: Filter[]): Filter[] {
  const ownerFilter = filters.find((filter) => filter.type === 'owner');
  if (!ownerFilter) {
    return filters;
  }
  ownerFilter.options = ownerFilter.options.map((currOption) => {
    return {
      ...currOption,
      selected: false,
    };
  });

  const updatedFilters = filters.map((filter) => {
    if (filter.type === ownerFilter.type) {
      return ownerFilter;
    }
    return filter;
  });

  return updatedFilters;
}

export function isFiltersSelectionEqual(filters1: Filter[] | null, filters2: Filter[] | null): boolean {
  const unwrappedFilters1 = !filters1 || isEmptyObject(filters1) ? [] : filters1;
  const unwrappedFilters2 = !filters2 || isEmptyObject(filters2) ? [] : filters2;

  const selectedOptions1 = unwrappedFilters1.flatMap((filter) => filter.options.filter((option) => option.selected));
  const selectedOptions2 = unwrappedFilters2.flatMap((filter) => filter.options.filter((option) => option.selected));

  if (selectedOptions1.length !== selectedOptions2.length) return false;

  const toKey = (opt: FilterOption): string => {
    const id = opt.metadata?.id ?? opt.metadata;
    return `${opt.type}:${id ?? opt.name}`;
  };

  const keys1 = selectedOptions1.map(toKey).sort();
  const keys2 = selectedOptions2.map(toKey).sort();

  return keys1.every((key, i) => key === keys2[i]);
}

const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};
